/**
 * instances.ts — Gathers repeated meshes and emits one `InstancedMesh` per
 * geometry+material.
 *
 * The town's small detail runs to well over a thousand pieces — window
 * surrounds, shutters, ridge tiles, flowers, and now every leaf-ball and weed
 * tuft in the back gardens. One `Mesh` each would be a thousand draw calls for
 * objects a few pixels across. Everything that repeats goes through here
 * instead, keyed by whatever makes two of them interchangeable (usually size
 * and colour), and comes out as a handful of batches.
 *
 * Callers hand over a finished transform rather than a position, so the same
 * collector can take a flat sill, a ridge tile rolled onto a slope, and a
 * squashed ball of foliage.
 *
 * Lives in its own module because both `buildings.ts` and `world.ts` need it —
 * the planting is as numerous as the windows, and the two shouldn't each grow
 * their own copy.
 */
import * as THREE from 'three';

const UP = new THREE.Vector3(0, 1, 0);
const ONE = new THREE.Vector3(1, 1, 1);
const SCRATCH = {
  m: new THREE.Matrix4(),
  q: new THREE.Quaternion(),
  p: new THREE.Vector3(),
  s: new THREE.Vector3(),
};

export class Instances {
  private groups = new Map<
    string,
    { geo: THREE.BufferGeometry; mat: THREE.Material; at: THREE.Matrix4[] }
  >();

  add(key: string, geo: THREE.BufferGeometry, mat: THREE.Material, matrix: THREE.Matrix4): void {
    let g = this.groups.get(key);
    if (!g) {
      g = { geo, mat, at: [] };
      this.groups.set(key, g);
    }
    g.at.push(matrix.clone());
  }

  /** Place one upright instance, optionally yawed to face out of a wall. */
  place(
    key: string,
    geo: THREE.BufferGeometry,
    mat: THREE.Material,
    x: number,
    y: number,
    z: number,
    ry = 0,
  ): void {
    SCRATCH.q.setFromAxisAngle(UP, ry);
    SCRATCH.p.set(x, y, z);
    this.add(key, geo, mat, SCRATCH.m.compose(SCRATCH.p, SCRATCH.q, ONE));
  }

  /**
   * The same, but squashed or stretched. Foliage needs it: a ball of leaves
   * flattened a little is the difference between a bush and a heap of marbles,
   * and scaling the instance is free where a second geometry is not.
   */
  placeScaled(
    key: string,
    geo: THREE.BufferGeometry,
    mat: THREE.Material,
    x: number,
    y: number,
    z: number,
    ry: number,
    sx: number,
    sy: number,
    sz: number,
  ): void {
    SCRATCH.q.setFromAxisAngle(UP, ry);
    SCRATCH.p.set(x, y, z);
    SCRATCH.s.set(sx, sy, sz);
    this.add(key, geo, mat, SCRATCH.m.compose(SCRATCH.p, SCRATCH.q, SCRATCH.s));
  }

  /** How many batches this will emit — one draw call each. */
  get batches(): number {
    return this.groups.size;
  }

  build(scene: THREE.Object3D): void {
    for (const g of this.groups.values()) {
      const mesh = new THREE.InstancedMesh(g.geo, g.mat, g.at.length);
      g.at.forEach((m, i) => mesh.setMatrixAt(i, m));
      mesh.instanceMatrix.needsUpdate = true;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      scene.add(mesh);
    }
  }
}
